import { useCallback, useEffect, useState, type FormEvent } from 'react';
import type { Invite, Role, User } from '@ia/shared';
import { api, errorMessage } from '../api';
import { useAuth } from '../auth';
import { Alert, ConfirmDialog, CopyButton, Empty, Field, Spinner, When } from '../components/ui';

export function UsersPage() {
  const { user: me, setUser: setMe } = useAuth();
  const [users, setUsers] = useState<User[] | null>(null);
  const [invites, setInvites] = useState<Invite[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [rowError, setRowError] = useState<{ id: string; message: string } | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [revoke, setRevoke] = useState<Invite | null>(null);
  const [revokeBusy, setRevokeBusy] = useState(false);
  const [revokeError, setRevokeError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [u, i] = await Promise.all([api.users(), api.invites()]);
      setUsers(u);
      setInvites(i);
      setError(null);
    } catch (e) {
      setError(errorMessage(e));
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  const patch = async (u: User, body: { role?: Role; disabled?: boolean }) => {
    setBusyId(u.id);
    setRowError(null);
    try {
      const updated = await api.updateUser(u.id, body);
      setUsers((rs) => rs?.map((r) => (r.id === updated.id ? updated : r)) ?? null);
      if (me && updated.id === me.id) setMe(updated);
    } catch (e) {
      setRowError({ id: u.id, message: errorMessage(e) });
    } finally {
      setBusyId(null);
    }
  };

  const now = Date.now();
  const pending = invites?.filter((i) => !i.acceptedAt && new Date(i.expiresAt).getTime() > now) ?? [];
  const past = invites?.filter((i) => i.acceptedAt || new Date(i.expiresAt).getTime() <= now) ?? [];

  return (
    <div className="page">
      <div className="page-head">
        <h1>Users</h1>
      </div>
      {error && <Alert>{error}</Alert>}

      <section className="section">
        <h2>Accounts</h2>
        {!users && !error && <Spinner />}
        {users && (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Last login</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className={u.disabled ? 'excluded' : undefined}>
                    <td>
                      {u.name} {me?.id === u.id && <span className="tag">you</span>}
                    </td>
                    <td>{u.email}</td>
                    <td>
                      <select
                        aria-label={`Role for ${u.email}`}
                        value={u.role}
                        disabled={busyId === u.id}
                        onChange={(e) => void patch(u, { role: e.target.value as Role })}
                      >
                        <option value="admin">admin</option>
                        <option value="member">member</option>
                      </select>
                    </td>
                    <td>{u.disabled ? <span className="pill pill-cancelled">Disabled</span> : <span className="pill pill-processed">Active</span>}</td>
                    <td>
                      <When iso={u.lastLoginAt} />
                    </td>
                    <td>
                      <When iso={u.createdAt} />
                    </td>
                    <td>
                      <button
                        className={u.disabled ? 'btn btn-sm' : 'btn btn-sm btn-danger-ghost'}
                        disabled={busyId === u.id}
                        onClick={() => void patch(u, { disabled: !u.disabled })}
                      >
                        {u.disabled ? 'Enable' : 'Disable'}
                      </button>
                      {rowError?.id === u.id && (
                        <div className="row-error" role="alert">
                          {rowError.message}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <InviteForm onCreated={(i) => setInvites((xs) => [i, ...(xs ?? [])])} />

      <section className="section">
        <h2>Pending invites ({pending.length})</h2>
        {invites && pending.length === 0 && <Empty>No pending invites.</Empty>}
        {pending.length > 0 && (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Invited</th>
                  <th>Expires</th>
                  <th>Invited by</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {pending.map((i) => (
                  <tr key={i.id}>
                    <td>{i.email}</td>
                    <td>
                      <span className={`role role-${i.role}`}>{i.role}</span>
                    </td>
                    <td>
                      <When iso={i.createdAt} />
                    </td>
                    <td>
                      <When iso={i.expiresAt} />
                    </td>
                    <td>{i.invitedBy ?? <span className="muted">—</span>}</td>
                    <td>
                      <button
                        className="btn btn-sm btn-danger-ghost"
                        onClick={() => {
                          setRevokeError(null);
                          setRevoke(i);
                        }}
                      >
                        Revoke
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {past.length > 0 && (
          <details className="details">
            <summary>Recent accepted / expired invites ({past.length})</summary>
            <ul className="plain-list">
              {past.map((i) => (
                <li key={i.id}>
                  {i.email} <span className="muted small">({i.role})</span> —{' '}
                  {i.acceptedAt ? <>accepted <When iso={i.acceptedAt} inline /></> : <>expired <When iso={i.expiresAt} inline /></>}
                </li>
              ))}
            </ul>
          </details>
        )}
      </section>

      <ConfirmDialog
        open={!!revoke}
        title="Revoke invite?"
        confirmLabel="Revoke"
        danger
        busy={revokeBusy}
        error={revokeError}
        onClose={() => setRevoke(null)}
        onConfirm={async () => {
          if (!revoke) return;
          setRevokeBusy(true);
          try {
            await api.revokeInvite(revoke.id);
            setInvites((xs) => xs?.filter((x) => x.id !== revoke.id) ?? null);
            setRevoke(null);
          } catch (e) {
            setRevokeError(errorMessage(e));
          } finally {
            setRevokeBusy(false);
          }
        }}
      >
        <p>
          The invite link sent to <strong>{revoke?.email}</strong> will stop working immediately.
        </p>
      </ConfirmDialog>
    </div>
  );
}

function InviteForm({ onCreated }: { onCreated: (i: Invite) => void }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('member');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<Invite | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const inv = await api.createInvite({ email: email.trim(), role });
      setCreated(inv);
      onCreated({ ...inv, inviteUrl: undefined });
      setEmail('');
      setRole('member');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="section">
      <h2>Invite someone</h2>
      <form className="inline-form" onSubmit={submit}>
        <Field label="Email">
          {(id) => <input id={id} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="name@example.com" />}
        </Field>
        <Field label="Role">
          {(id) => (
            <select id={id} value={role} onChange={(e) => setRole(e.target.value as Role)}>
              <option value="member">member — view batches, replay</option>
              <option value="admin">admin — also manage users, PATs, settings</option>
            </select>
          )}
        </Field>
        <button type="submit" className="btn btn-primary" disabled={busy}>
          {busy ? 'Creating…' : 'Create invite'}
        </button>
      </form>
      {error && <Alert>{error}</Alert>}
      {created?.inviteUrl && (
        <div className="invite-result" role="status">
          <div>
            Invite link for <strong>{created.email}</strong> ({created.role}):
          </div>
          <div className="copy-row">
            <input readOnly value={created.inviteUrl} aria-label="Invite link" onFocus={(e) => e.currentTarget.select()} />
            <CopyButton text={created.inviteUrl} />
          </div>
          <div className="muted small">
            Send this link to the person directly. It expires in 7 days (<When iso={created.expiresAt} inline />) and{' '}
            <strong>won't be shown again</strong>.
          </div>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setCreated(null)}>
            Dismiss
          </button>
        </div>
      )}
    </section>
  );
}
